import { FilePurpose, languageDisplayCode, LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { CourseContentType, CourseFormState } from '@/client/courses';
import {
  attachmentTypeOptions,
  InteractiveElement,
  InteractiveElementType,
} from '@/client/courses/types/courses.type';
import { File } from '@/client/files';
import {
  AnswerOptionType,
  ModifiedAnswerOption,
  ModifiedQuestion,
  QuizType,
} from '@/client/quizzes';
import { LoadingStatuses } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { renderToolbar } from '@/components/courses/ContentEditorQuillBubbleToolbar';
import { UnsavedChangesModal } from '@/components/courses/modals/UnsavedChangesModal';
import { AttachmentTypeMenu } from '@/components/slides/admin/AttachmentTypeMenu';
import { ImageSettingsPanel } from '@/components/slides/admin/ImageSettingsPanel';
import { SlideImageUpload } from '@/components/slides/admin/SlideImageUpload';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { EditableText } from '@/ui/editable-text';
import { LanguageTabs } from '@/ui/language-tabs';
import { FlexContainer } from '@/ui/styled-ui';
import {
  convertQuestionsToArrayFormat,
  getFullyTranslatedLanguages,
  GlobeIcon,
  ImageUploadIcon,
  modifyCourseQuiz,
  PencilIcon,
  sortLanguagesWithPriority,
  TrashIcon,
} from '@/utils/helpers';
import { setupColorPickerForBubble } from '@/utils/helpers/quill.helper';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { set } from 'lodash';
import { Checkbox } from 'primereact/checkbox';
import { Dialog, DialogProps } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Panel, PanelHeaderTemplateOptions } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 650px;
  max-height: 520px;

  &.v2 {
    .actions {
      margin-top: var(--semi-big-padding);
    }

    .p-dialog-title h1 {
      font-weight: 600;
      font-size: var(--heading-small-font-size);
    }
  }
`;

const StyledPanel = styled(Panel)`
  position: relative;
  margin-top: var(--default-padding);

  .p-panel-header {
    border-radius: var(--xsmall-border-radius);
    border: 1px solid var(--white-main);
    background: var(--beige-main);
    height: 90px;
    padding: 0 var(--semi-big-padding);

    &.collapsed {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
  .p-panel-content {
    background: var(--beige-main);
    border: 0;
    border-top: 1px solid var(--gray-opacity);
    padding: var(--default-padding) var(--semi-big-padding);

    label {
      color: var(--black-main);
    }
  }

  .upload > div {
    background: var(--white-main);
  }

  .option-text {
    border-radius: var(--xsmall-border-radius);
    border: 1px solid var(--gray-dark);
    background: var(--white-main);
  }

  .p-inputtext {
    border-radius: var(--small-border-radius) !important;
  }

  .quill {
    height: 100% !important;
    background-color: var(--white-main) !important;
    border-radius: var(--small-border-radius) !important;
    min-height: auto;
    color: var(--black-main) !important;

    &.email-header {
      .ql-editor {
        min-height: 70px;
      }
    }

    &.email-body {
      .ql-editor {
        min-height: 150px;
      }
    }

    &.sms-message {
      .ql-editor {
        min-height: 100px;
      }
    }

    * {
      font-size: var(--small-font-size) !important;
    }
  }
`;

const StyledDeleteContainer = styled(FlexContainer)`
  cursor: pointer;

  &:hover {
    svg path,
    span {
      fill: var(--red-main);
      color: var(--red-main);
    }
  }

  > span {
    font-size: var(--xxsmall-font-size);
    margin-left: var(--xsmall-padding);
  }
`;

const AttachmentDeleteButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-darker);
  font-size: var(--xsmall-font-size);
  padding: 0;

  &:hover {
    color: var(--red-main);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledTitle = styled.div`
  max-width: calc(100% - 120px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--black-main);
  font-size: var(--small-font-size);
  font-weight: 500;
  flex: 1;
`;

const StyledAddButton = styled(FlexContainer)`
  height: 94px;
  align-items: center;
  flex-shrink: 0;
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--gray-dark);
  background: var(--white-main);
  color: var(--gray-darker);
  font-weight: 500;
  margin-top: var(--semi-big-padding);
  cursor: pointer;
  transition: background 0.3s ease;

  i {
    color: var(--black-main);
  }

  &:hover {
    background: var(--gray-pale);
  }

  &.sm {
    height: 60px;
    margin-top: 24px;
  }
`;

type ContentQuizModalProps = {
  index?: number;
  values: CourseFormState;
  visible: boolean;
  isDisabled: boolean;
  onHide: () => void;
  setFieldValue: (field: string, value: any) => void;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
};

export const ContentQuizModal: React.FC<
  ContentQuizModalProps & DialogProps
> = ({
  index = 0,
  values,
  visible,
  isDisabled,
  setFieldValue,
  setIsSubmitDisabled,
  onHide,
}) => {
  const { id: courseId } = useParams();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const [questions, setQuestions] = useState<ModifiedQuestion[]>([]);
  const [tempQuestions, setTempQuestions] = useState<ModifiedQuestion[]>([]);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedTab, setSelectedTab] = useState<keyof typeof LanguagesEnum>(
    languageDisplayCode[LanguagesEnum.EN] as keyof typeof LanguagesEnum,
  );

  const [requiredTranslations, setRequiredTranslations] = useState([
    languageDisplayCode[LanguagesEnum.EN],
  ]);

  const [openImageSettings, setOpenImageSettings] = useState<number | null>(
    null,
  );

  useEffect(() => {
    const fetchQuiz = async () => {
      const shouldFetch =
        !!values.courseContents?.[index]?.id &&
        !values.courseContents?.[index]?.id?.includes('temp');

      if (courseId && shouldFetch) {
        setIsLoading(true);
        try {
          const quizData = await client.courses.getCourseQuiz(
            courseId,
            values.courseContents[index]?.quiz?.id || '',
          );

          const modifiedQuiz = modifyCourseQuiz(quizData);

          if (modifiedQuiz) {
            setQuestions(modifiedQuiz.questions || []);
            setTempQuestions(
              JSON.parse(JSON.stringify(modifiedQuiz.questions || [])),
            );
            setItemName(modifiedQuiz.title || generateItemTitle());
          }
        } catch (e) {
          handleAxiosError(e as AxiosError, toast);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (visible) {
      setItemName(
        values.courseContents[index]?.quiz?.title || generateItemTitle(),
      );

      fetchQuiz();
    }
  }, [visible, courseId, index]);

  useEffect(() => {
    if (values?.courseLanguages) {
      setRequiredTranslations(
        values.courseLanguages.map(
          (languageCode: string) => languageDisplayCode[languageCode],
        ),
      );
    }

    const tab = values.courseLanguages?.length
      ? sortLanguagesWithPriority(
          values.courseLanguages?.map(
            (languageCode: string) => languageDisplayCode[languageCode],
          ),
        )[0]
      : languageDisplayCode[LanguagesEnum.EN];
    setSelectedTab(tab as keyof typeof LanguagesEnum);
  }, [values.courseLanguages]);

  const generateItemTitle = () => {
    const title = values?.courseContents[index]?.title;

    return title
      ? title
      : t('content.name.your.item', {
          contentItem: t('content.quiz'),
        });
  };

  const [itemName, setItemName] = useState<string>(
    values.courseContents[index]?.quiz?.title || generateItemTitle(),
  );

  const emailHeaderQuillRefs = useRef<Map<string, any>>(new Map());
  const emailBodyQuillRefs = useRef<Map<string, any>>(new Map());
  const smsMessageQuillRefs = useRef<Map<string, any>>(new Map());
  const colorPickerCleanupsRef = useRef<Map<string, (() => void) | undefined>>(
    new Map(),
  );

  const setupColorPickerForEditor = (
    editorKey: string,
    quillRef: React.RefObject<any>,
    colorButtonId: string,
    colorInputId: string,
  ) => {
    if (!quillRef.current) return;

    const existingCleanup = colorPickerCleanupsRef.current.get(editorKey);
    if (existingCleanup) {
      existingCleanup();
      colorPickerCleanupsRef.current.delete(editorKey);
    }

    const cleanup = setupColorPickerForBubble(
      quillRef,
      colorButtonId,
      colorInputId,
    );

    if (cleanup) {
      colorPickerCleanupsRef.current.set(editorKey, cleanup);
    }
  };

  const createQuillRefCallback = (
    editorKey: string,
    quillRefsMap: React.MutableRefObject<Map<string, any>>,
    colorButtonId: string,
    colorInputId: string,
    initialValue?: string,
  ) => {
    return (el: ReactQuill | null) => {
      if (el) {
        const editor = el.getEditor();
        const existingEditor = quillRefsMap.current.get(editorKey);
        if (existingEditor !== editor) {
          quillRefsMap.current.set(editorKey, editor);

          if (initialValue && editor && editor.root) {
            const currentContent = editor.root.innerHTML;
            if (currentContent !== initialValue && !editor.hasFocus()) {
              setTimeout(() => {
                // Double-check focus state before updating
                if (
                  !editor.hasFocus() &&
                  editor.root.innerHTML !== initialValue
                ) {
                  editor.root.innerHTML = initialValue;
                }
              }, 0);
            }
          }
          setTimeout(() => {
            const tempRef = { current: editor };
            setupColorPickerForEditor(
              editorKey,
              tempRef,
              colorButtonId,
              colorInputId,
            );
          }, 0);
        }
      } else {
        quillRefsMap.current.delete(editorKey);
      }
    };
  };

  const updateInteractiveElementData = (
    questionIndex: number,
    dataKey: string,
    value: string,
    currentData: Record<string, any>,
  ) => {
    updateField(
      questionIndex,
      `.translations.${LanguagesEnum[selectedTab]}.interactiveElement.data`,
      {
        ...currentData,
        [dataKey]: value,
      },
    );
  };

  const renderEmailEditors = (
    question: ModifiedQuestion,
    questionIndex: number,
    questionId: string | number,
  ) => {
    const currentData =
      question.translations?.[LanguagesEnum[selectedTab]]?.interactiveElement
        ?.data || {};

    const attachmentIcon = currentData.attachment
      ? attachmentTypeOptions(t).find(
          (option) => option.value === currentData.attachment?.type,
        )?.icon
      : null;

    return (
      <div key={`email-editors-${questionId}-${selectedTab}`}>
        {renderToolbar(questionId, 'email-header')}
        {renderToolbar(questionId, 'email-body')}
        {renderQuillEditor(
          questionIndex,
          question,
          `email-header-${questionId}`,
          'email-header',
          'header',
          emailHeaderQuillRefs,
          'content.question.email.header',
          'content.question.email.header.placeholder',
        )}
        {currentData.attachment && (
          <div className="field w-full mb-4">
            <label>{t('content.question.email.attachment')}</label>
            <FlexContainer align="center" gap={8} className="mt-2 w-full">
              {attachmentIcon && attachmentIcon}
              <InputText
                value={currentData.attachment.label}
                placeholder={t('content.email.attachment.placeholder')}
                className="white w-full"
                onChange={(e) => {
                  const updatedAttachment = {
                    ...currentData.attachment,
                    label: e.target.value,
                  };

                  updateField(
                    questionIndex,
                    `.translations.${LanguagesEnum[selectedTab]}.interactiveElement.data.attachment`,
                    updatedAttachment,
                  );
                }}
              />
              <AttachmentDeleteButton
                type="button"
                onClick={() => {
                  updateField(
                    questionIndex,
                    `.translations.${LanguagesEnum[selectedTab]}.interactiveElement.data.attachment`,
                    undefined,
                  );
                }}
              >
                <i className="pi pi-times" />
              </AttachmentDeleteButton>
            </FlexContainer>
          </div>
        )}
        {renderQuillEditor(
          questionIndex,
          question,
          `email-body-${questionId}`,
          'email-body',
          'body',
          emailBodyQuillRefs,
          'content.question.email.body',
          'content.question.email.body.placeholder',
        )}
      </div>
    );
  };

  const renderSMSEditors = (
    question: ModifiedQuestion,
    questionIndex: number,
    questionId: string | number,
    currentData: Record<string, any>,
  ) => {
    return (
      <div key={`sms-editors-${questionId}-${selectedTab}`}>
        {renderToolbar(questionId, 'sms-message')}
        <div className="field w-full mb-4">
          <label>{t('content.question.sms.sender')}</label>
          <InputText
            key={`sms-sender-${questionId}-${selectedTab}`}
            placeholder={t('content.question.sms.sender.placeholder')}
            className="white w-full"
            value={currentData.sender || ''}
            onChange={(e) => {
              updateInteractiveElementData(
                questionIndex,
                'sender',
                e.target.value,
                currentData,
              );
            }}
          />
        </div>
        {renderQuillEditor(
          questionIndex,
          question,
          `sms-message-${questionId}`,
          'sms-message',
          'message',
          smsMessageQuillRefs,
          'content.question.sms.message',
          'content.question.sms.message.placeholder',
        )}
      </div>
    );
  };

  const renderInteractiveElementEditors = (
    question: ModifiedQuestion,
    questionIndex: number,
  ) => {
    const interactiveElement =
      question?.translations?.[LanguagesEnum[selectedTab]]?.interactiveElement;
    const elementType = interactiveElement?.type;
    const questionId = question.id || questionIndex;
    const currentData = (interactiveElement?.data as Record<string, any>) || {};

    if (elementType === InteractiveElementType.EMAIL) {
      return renderEmailEditors(question, questionIndex, questionId);
    }

    if (elementType === InteractiveElementType.SMS) {
      return renderSMSEditors(question, questionIndex, questionId, currentData);
    }

    return null;
  };

  const renderQuillEditor = (
    questionIndex: number,
    question: ModifiedQuestion,
    editorKey: string,
    editorType: 'email-header' | 'email-body' | 'sms-message',
    dataKey: 'header' | 'body' | 'message',
    quillRefsMap: React.MutableRefObject<Map<string, any>>,
    labelKey: string,
    placeholderKey: string,
  ) => {
    const questionId = question.id || questionIndex;
    const toolbarId = `quill-bubble-toolbar-${editorType}-${questionId}`;
    const colorButtonId = `custom-color-btn-${editorType}-${questionId}`;
    const colorInputId = `custom-color-input-${editorType}-${questionId}`;
    const currentData =
      question.translations[LanguagesEnum[selectedTab]]?.interactiveElement
        ?.data || {};
    const value = currentData[dataKey] || '';

    return (
      <div className="field w-full mb-4" key={`${editorKey}-field`}>
        <label>{t(labelKey)}</label>
        <ReactQuill
          key={`${editorKey}-${selectedTab}`}
          className={`quill-editor ${editorType}`}
          theme="bubble"
          value={value}
          onChange={(html: string, delta: any, source: string) => {
            if (source === 'user' || source === 'api') {
              if (html !== value) {
                updateInteractiveElementData(
                  questionIndex,
                  dataKey,
                  html,
                  currentData,
                );
              }
            }
          }}
          modules={{
            toolbar: {
              container: `#${toolbarId}`,
            },
            clipboard: {
              matchVisual: false,
            },
          }}
          placeholder={t(placeholderKey)}
          ref={createQuillRefCallback(
            editorKey,
            quillRefsMap,
            colorButtonId,
            colorInputId,
            value,
          )}
        />
      </div>
    );
  };

  useEffect(() => {
    return () => {
      colorPickerCleanupsRef.current.forEach((cleanup) => cleanup?.());
      colorPickerCleanupsRef.current.clear();
      emailHeaderQuillRefs.current.clear();
      emailBodyQuillRefs.current.clear();
      smsMessageQuillRefs.current.clear();
    };
  }, [questions, selectedTab]);

  const headerTemplate = () => (
    <FlexContainer wrap="wrap">
      {!isLoading && (
        <h1 className="flex-1 w-full">
          <EditableText
            key={itemName}
            value={itemName}
            icon={<PencilIcon />}
            saveValue={(value: string) => {
              if (value !== itemName) {
                setFieldValue(`courseContents.${index}.isAutoNamed`, false);
              }
              setItemName(value);
            }}
          />
        </h1>
      )}
    </FlexContainer>
  );

  const panelHeaderTemplate = (
    options: PanelHeaderTemplateOptions,
    index: number,
    text: string,
  ) => {
    const questionLabel = t('content.question.number', { number: index + 1 });
    const className = `${options.className} justify-content-space-between ${
      !options.collapsed ? 'collapsed' : ''
    }`;

    return (
      <FlexContainer className={className} gap={40}>
        <StyledTitle>
          {text ? (
            <>
              <strong>{questionLabel}:</strong> {text}
            </>
          ) : (
            <strong>{questionLabel}</strong>
          )}
        </StyledTitle>

        <FlexContainer className="flex-shrink-0" width="auto">
          <StyledDeleteContainer
            justify="flex-end"
            className="ml-auto mr-3"
            width="auto"
            onClick={() => {
              setDialogData({
                show: true,
                type: DialogType.CONFIRMATION,
                header: t('dialog.delete'),
                message: t('content.question.delete.confirm'),
                confirmButtonLabel: t('button.delete'),
                cancelButtonLabel: t('button.cancel'),
                onAccept: () =>
                  setQuestions((prevQuestions) =>
                    prevQuestions.filter((_, i) => i !== index),
                  ),
              });
            }}
          >
            <TrashIcon />
            <span>{t('content.delete.question')}</span>
          </StyledDeleteContainer>
          {options.togglerElement}
        </FlexContainer>
      </FlexContainer>
    );
  };

  const updateField = (
    questionIndex: number,
    path: string,
    value?: string | File | boolean | null | InteractiveElement | object,
  ) => {
    setQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      set(updatedQuestions, `[${questionIndex}]${path}`, value);
      return updatedQuestions;
    });
  };

  const createEmptyAnswerOption = (): ModifiedAnswerOption => ({
    translations: {
      [LanguagesEnum[selectedTab]]: {
        text: '',
      },
    },
    isCorrect: false,
    position: 0,
    type: AnswerOptionType.STANDARD,
  });

  const addAnswerOption = (questionIndex: number) => {
    setQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      const currentOptions =
        updatedQuestions[questionIndex].answerOptions || [];
      updatedQuestions[questionIndex].answerOptions = [
        ...currentOptions,
        createEmptyAnswerOption(),
      ];

      return updatedQuestions;
    });
  };

  const handleSaveQuiz = async () => {
    if (!courseId) return;

    const invalidQuestions = questions.filter((question) => {
      const correctOptions =
        question.answerOptions?.filter((option) => option.isCorrect) || [];
      return correctOptions.length === 0;
    });

    if (invalidQuestions.length > 0) {
      toast?.error(
        t('toast.error'),
        t('content.validation.oneCorrectAnswerRequired'),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const quizData = {
        title: itemName,
        questions: convertQuestionsToArrayFormat(
          questions,
          false,
          values.courseLanguages,
        ),
        supportedLanguages: values.courseLanguages,
        type: QuizType.TRADITIONAL,
        account:
          currentAccount?.isSystem || !currentAccount?.id
            ? null
            : currentAccount?.id,
      };

      let response;
      const quizId = values.courseContents?.[index]?.quiz?.id;
      if (quizId) {
        response = await client.quizzes.updateQuiz(quizId, quizData);
      } else {
        response = await client.quizzes.createQuiz(quizData);
      }

      const updatedContents = [...values.courseContents];
      updatedContents[index] = {
        ...updatedContents[index],
        type: CourseContentType.QUIZ,
        quiz: response,
        position: index,
      };
      setFieldValue('courseContents', updatedContents);

      const modifiedQuiz = modifyCourseQuiz(response);
      if (modifiedQuiz) {
        setQuestions(modifiedQuiz.questions || []);
        setTempQuestions(
          JSON.parse(JSON.stringify(modifiedQuiz.questions || [])),
        );
      } else {
        setTempQuestions(JSON.parse(JSON.stringify(questions)));
      }

      onHide();
    } catch (e) {
      handleAxiosError(e as AxiosError, toast);
    } finally {
      setIsSubmitting(false);
    }
  };

  const imageSettingsTemplate = (question: ModifiedQuestion, index: number) => {
    if (!question.translations?.[LanguagesEnum[selectedTab]]?.file) {
      return undefined;
    }

    return (
      <ImageSettingsPanel
        key={`${question.id}-${LanguagesEnum[selectedTab]}-image-settings`}
        imageSettings={{
          withBorder:
            question?.translations?.[LanguagesEnum[selectedTab]]?.imageSettings
              ?.withBorder || false,
          withZoom:
            question?.translations?.[LanguagesEnum[selectedTab]]?.imageSettings
              ?.withZoom || false,
        }}
        isOpen={openImageSettings === index}
        onOpen={() => {
          setOpenImageSettings(index);
        }}
        onClose={() => {
          setOpenImageSettings(null);
        }}
        onBorderChange={(withBorder: boolean) => {
          updateField(
            index,
            `.translations.${LanguagesEnum[selectedTab]}.imageSettings.withBorder`,
            withBorder,
          );
        }}
        onZoomChange={(withZoom: boolean) => {
          updateField(
            index,
            `.translations.${LanguagesEnum[selectedTab]}.imageSettings.withZoom`,
            withZoom,
          );
        }}
      />
    );
  };

  return (
    <>
      <StyledDialog
        blockScroll
        visible={visible}
        onHide={() => {
          if (JSON.stringify(tempQuestions) !== JSON.stringify(questions)) {
            setShowUnsavedChangesModal(true);
          } else {
            onHide();
          }
        }}
        header={headerTemplate}
        draggable={false}
        className="v2"
        data-testid="content-quiz-modal"
      >
        {isLoading ? (
          <FlexContainer justify="center" align="center" className="h-full">
            <ProgressSpinner />
          </FlexContainer>
        ) : (
          <>
            <FlexContainer justify="flex-start" direction="column" gap={16}>
              <FlexContainer justify="flex-start" gap={8}>
                <GlobeIcon />
                {t('content.chosen.languages')}
              </FlexContainer>
              <LanguageTabs
                options={values?.courseLanguages || []}
                requiredOptions={requiredTranslations}
                validOptions={getFullyTranslatedLanguages(
                  values?.courseLanguages || [],
                  questions,
                )}
                isDisabled={isDisabled}
                onToggle={(option) =>
                  setSelectedTab(option as keyof typeof LanguagesEnum)
                }
                defaultSelected={selectedTab}
              />
            </FlexContainer>

            {questions.map((question: ModifiedQuestion, index: number) => (
              <StyledPanel
                key={index}
                headerTemplate={(options) =>
                  panelHeaderTemplate(
                    options,
                    index,
                    question?.translations?.[LanguagesEnum[selectedTab]]
                      ?.text || '',
                  )
                }
                toggleable
                collapseIcon="pi pi-chevron-up"
                expandIcon="pi pi-chevron-down"
              >
                <div className="field">
                  <label>{t('content.question.text')}</label>
                  <InputTextarea
                    key={selectedTab}
                    placeholder={t('content.question.text.placeholder')}
                    value={
                      question?.translations?.[LanguagesEnum[selectedTab]]?.text
                    }
                    className="w-full white"
                    autoResize
                    onChange={(e) => {
                      updateField(
                        index,
                        `.translations.${LanguagesEnum[selectedTab]}.text`,
                        e.target.value,
                      );
                    }}
                  />
                </div>

                <div className="field w-full mb-4">
                  <SlideImageUpload
                    key={selectedTab}
                    preselectedFile={
                      question?.translations?.[LanguagesEnum[selectedTab]]?.file
                    }
                    preselectedInteractiveElement={
                      question?.translations?.[LanguagesEnum[selectedTab]]
                        ?.interactiveElement || null
                    }
                    label={t('content.question.image')}
                    icon={<ImageUploadIcon />}
                    setIsSubmitDisabled={setIsSubmitDisabled}
                    isDisabled={isDisabled}
                    additionalImgOptions={
                      <>
                        {imageSettingsTemplate(question, index)}
                        {question.translations?.[LanguagesEnum[selectedTab]]
                          ?.interactiveElement?.type ===
                          InteractiveElementType.EMAIL && (
                          <AttachmentTypeMenu
                            attachment={
                              question.translations?.[
                                LanguagesEnum[selectedTab]
                              ]?.interactiveElement?.data?.attachment || null
                            }
                            triggerClassName="m-auto"
                            menuId={`quiz-email-attachment-type-menu-${question.id}-${selectedTab}`}
                            onChange={(attachment) => {
                              updateField(
                                index,
                                `.translations.${LanguagesEnum[selectedTab]}.interactiveElement.data.attachment`,
                                attachment || undefined,
                              );
                            }}
                          />
                        )}
                      </>
                    }
                    purpose={FilePurpose.QUIZZES_MATERIALS}
                    onFileUpload={(file: File | null) => {
                      updateField(
                        index,
                        `.translations.${LanguagesEnum[selectedTab]}.file`,
                        file,
                      );
                      updateField(
                        index,
                        `.translations.${LanguagesEnum[selectedTab]}.imageSettings.withBorder`,
                        true,
                      );
                      if (file) {
                        updateField(
                          index,
                          `.translations.${LanguagesEnum[selectedTab]}.interactiveElement`,
                          null,
                        );
                      }
                    }}
                    onInteractiveElementUpdate={(
                      interactiveElement: InteractiveElement | undefined,
                    ) => {
                      updateField(
                        index,
                        `.translations.${LanguagesEnum[selectedTab]}.interactiveElement`,
                        interactiveElement,
                      );
                      if (interactiveElement) {
                        updateField(
                          index,
                          `.translations.${LanguagesEnum[selectedTab]}.file`,
                          null,
                        );
                      }
                    }}
                  />
                </div>

                {renderInteractiveElementEditors(question, index)}

                <div className="field w-full mb-4">
                  <label>{t('content.answer.options')}</label>
                  {questions?.[index]?.answerOptions?.map(
                    (option: ModifiedAnswerOption, optionIndex: number) => (
                      <FlexContainer
                        key={optionIndex}
                        gap={18}
                        className="mt-2"
                      >
                        <Checkbox
                          checked={!!option.isCorrect}
                          onChange={(e) => {
                            updateField(
                              index,
                              `.answerOptions[${optionIndex}].isCorrect`,
                              e.checked,
                            );
                          }}
                        />
                        <InputText
                          key={selectedTab}
                          value={
                            question.answerOptions[optionIndex]?.translations?.[
                              LanguagesEnum[selectedTab]
                            ]?.text
                          }
                          className="option-text flex-1"
                          placeholder={t('content.answer.option.placeholder')}
                          onChange={(e) => {
                            updateField(
                              index,
                              `.answerOptions[${optionIndex}].translations.${LanguagesEnum[selectedTab]}.text`,
                              e.target.value,
                            );
                          }}
                        />
                        <StyledDeleteContainer
                          className="delete ml-auto w-auto"
                          onClick={() => {
                            setQuestions((prevQuestions) => {
                              const updatedQuestions = [...prevQuestions];
                              const path = `[${index}].answerOptions`;
                              const newOptions = [
                                ...updatedQuestions[index].answerOptions,
                              ];

                              newOptions.splice(optionIndex, 1);
                              set(updatedQuestions, path, newOptions);

                              return updatedQuestions;
                            });
                          }}
                        >
                          <TrashIcon />
                        </StyledDeleteContainer>
                      </FlexContainer>
                    ),
                  )}
                  <StyledAddButton
                    justify="center"
                    align="center"
                    className="sm"
                    onClick={() => addAnswerOption(index)}
                  >
                    <i className="pi pi-plus" />
                    <span className="ml-2">{t('content.add.option')}</span>
                  </StyledAddButton>
                </div>

                <div className="field">
                  <label>{t('content.explanation')}</label>
                  <InputTextarea
                    key={selectedTab}
                    placeholder={t('content.explanation.placeholder')}
                    value={
                      question?.translations?.[LanguagesEnum[selectedTab]]
                        ?.explanation
                    }
                    className="w-full white"
                    autoResize
                    onChange={(e) => {
                      updateField(
                        index,
                        `.translations.${LanguagesEnum[selectedTab]}.explanation`,
                        e?.target?.value,
                      );
                    }}
                  />
                </div>

                {currentAccount?.isSystem && (
                  <div className="field">
                    <FlexContainer justify="space-between" align="center">
                      <label className="mb-0">
                        {t('content.isAiCustomizationEnabled')}
                      </label>
                      <InputSwitch
                        checked={question.isAiCustomizationEnabled !== false}
                        onChange={(e) => {
                          updateField(
                            index,
                            '.isAiCustomizationEnabled',
                            e.value ?? false,
                          );
                        }}
                      />
                    </FlexContainer>
                    {question.isAiCustomizationEnabled !== false && (
                      <InputTextarea
                        placeholder={t(
                          'content.aiCustomizationInstructions.placeholder',
                        )}
                        value={question.aiCustomizationInstructions || ''}
                        className="w-full white mt-2"
                        autoResize
                        onChange={(e) => {
                          updateField(
                            index,
                            '.aiCustomizationInstructions',
                            e.target.value,
                          );
                        }}
                      />
                    )}
                  </div>
                )}
              </StyledPanel>
            ))}

            <StyledAddButton
              justify="center"
              align="center"
              onClick={() => {
                setQuestions((prevQuestions) => [
                  ...prevQuestions,
                  {
                    answerOptions: [],
                    translations: {},
                    isAiCustomizationEnabled: false,
                  } as ModifiedQuestion,
                ]);
              }}
            >
              <i className="pi pi-plus" />
              <span className="ml-2">{t('content.add.question')}</span>
            </StyledAddButton>

            <div className="mt-auto">
              <FlexContainer justify="flex-start" className="actions">
                <>
                  <AppButton
                    label={t('button.cancel')}
                    type="outlined"
                    className="mr-3"
                    isDisabled={isSubmitting}
                    onClick={() => {
                      if (
                        JSON.stringify(tempQuestions) !==
                        JSON.stringify(questions)
                      ) {
                        setShowUnsavedChangesModal(true);
                      } else {
                        onHide();
                      }
                    }}
                  />

                  <AppButton
                    state={
                      isSubmitting
                        ? LoadingStatuses.LOADING
                        : LoadingStatuses.IDLE
                    }
                    label={t('button.save')}
                    onClick={handleSaveQuiz}
                    isDisabled={isSubmitting}
                  />
                </>
              </FlexContainer>
            </div>
          </>
        )}
      </StyledDialog>

      <UnsavedChangesModal
        visible={showUnsavedChangesModal}
        message={t('content.quiz.unsavedChanges.confirm')}
        onHide={() => setShowUnsavedChangesModal(false)}
        onSave={handleSaveQuiz}
        onDiscard={() => {
          setQuestions(tempQuestions);
          onHide();
        }}
      />
    </>
  );
};
